<template>
    <span class="atlas-chat-loader-anchor" aria-hidden="true" />
</template>

<script>
const ATLAS_WIDGET_ID = "atlasChatWidget";
const ATLAS_INLINE_SCRIPT_ID = "atlas-chat-widget-inline-script";

export default {
    name: "AtlasChatWidgetLoader",
    methods: {
        getWidgetHtmlUrl() {
            const base = process.env.BASE_URL || "/";
            const normalizedBase = base.endsWith("/") ? base : `${base}/`;
            return `${normalizedBase}atlas-chat-widget.html`;
        },
        markWidgetVisible() {
            const widget = document.getElementById(ATLAS_WIDGET_ID);
            if (!widget) return;
            widget.classList.remove("atlas-chat-hidden");
            widget.setAttribute("aria-hidden", "false");
        },
        markWidgetHidden() {
            const widget = document.getElementById(ATLAS_WIDGET_ID);
            if (!widget) return;
            widget.classList.add("atlas-chat-hidden");
            widget.setAttribute("aria-hidden", "true");
        },
        async ensureExternalScript(src) {
            const selector = `script[data-atlas-external-src="${src}"]`;
            const existing = document.querySelector(selector);
            if (existing) return;

            await new Promise((resolve, reject) => {
                const script = document.createElement("script");
                script.src = src;
                script.async = false;
                script.dataset.atlasExternalSrc = src;
                script.onload = () => resolve();
                script.onerror = () => reject(new Error(`Failed to load external script: ${src}`));
                document.body.appendChild(script);
            });
        },
        appendStyles(doc) {
            const styleNodes = Array.from(doc.querySelectorAll("style"));
            styleNodes.forEach((node, index) => {
                const styleId = `atlas-chat-widget-style-${index}`;
                if (document.getElementById(styleId)) return;
                const style = document.createElement("style");
                style.id = styleId;
                style.textContent = node.textContent || "";
                document.head.appendChild(style);
            });
        },
        appendLinks(doc) {
            const linkNodes = Array.from(doc.querySelectorAll('link[rel="stylesheet"]'));
            linkNodes.forEach((node) => {
                const href = node.getAttribute("href");
                if (!href) return;
                if (document.querySelector(`link[data-atlas-link-href="${href}"]`)) return;
                const link = document.createElement("link");
                link.rel = "stylesheet";
                link.href = href;
                link.dataset.atlasLinkHref = href;
                document.head.appendChild(link);
            });
        },
        appendWidgetMarkup(doc) {
            if (document.getElementById(ATLAS_WIDGET_ID)) return;
            const widgetRoot = doc.getElementById(ATLAS_WIDGET_ID);
            if (!widgetRoot) return;
            document.body.appendChild(widgetRoot.cloneNode(true));
        },
        async appendScripts(doc) {
            const scriptNodes = Array.from(doc.querySelectorAll("script"));
            const externalScripts = scriptNodes.filter((node) => node.getAttribute("src"));
            const inlineScripts = scriptNodes.filter((node) => !node.getAttribute("src"));

            for (const node of externalScripts) {
                const src = node.getAttribute("src");
                if (src) {
                    // Preserve load order required by marked + DOMPurify before widget inline script.
                    // eslint-disable-next-line no-await-in-loop
                    await this.ensureExternalScript(src);
                }
            }

            if (!inlineScripts.length) return;
            if (document.getElementById(ATLAS_INLINE_SCRIPT_ID)) return;

            const inlineScript = document.createElement("script");
            inlineScript.id = ATLAS_INLINE_SCRIPT_ID;
            inlineScript.textContent = inlineScripts.map((node) => node.textContent || "").join("\n");
            document.body.appendChild(inlineScript);
        },
        async ensureWidgetMounted() {
            if (window.__atlasWidgetMounted) {
                this.markWidgetVisible();
                return;
            }

            const response = await fetch(this.getWidgetHtmlUrl(), { cache: "no-cache" });
            if (!response.ok) {
                throw new Error(`Unable to load Atlas widget HTML (${response.status})`);
            }
            const html = await response.text();
            const doc = new window.DOMParser().parseFromString(html, "text/html");

            this.appendLinks(doc);
            this.appendStyles(doc);
            this.appendWidgetMarkup(doc);
            await this.appendScripts(doc);

            window.__atlasWidgetMounted = true;
            this.markWidgetVisible();
        },
    },
    async mounted() {
        try {
            await this.ensureWidgetMounted();
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error("Failed to initialize Atlas chat widget:", error);
        }
    },
    beforeDestroy() {
        this.markWidgetHidden();
    },
};
</script>

<style scoped>
.atlas-chat-loader-anchor {
    display: none;
}
</style>
